import React, { useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Share2, X, Copy, Check, Facebook, Twitter, Mail, MessageCircle, Send } from "lucide-react";

const canonical = (url) => {
  if (url) return url;
  if (typeof window === "undefined") return "";
  return window.location.origin + window.location.pathname;
};

export function ShareModal({ open, onClose, url, title = "ItsAllAboutJesus Outreach and Help Center", text = "" }) {
  const [copied, setCopied] = useState(false);
  if (!open) return null;
  const link = canonical(url);
  const enc = encodeURIComponent(link);
  const encText = encodeURIComponent(text || title);

  const targets = [
    { name: "Facebook", Icon: Facebook, color: "#1877F2", href: `https://www.facebook.com/sharer/sharer.php?u=${enc}` },
    { name: "X", Icon: Twitter, color: "#0F172A", href: `https://twitter.com/intent/tweet?url=${enc}&text=${encText}` },
    { name: "WhatsApp", Icon: MessageCircle, color: "#25D366", href: `https://wa.me/?text=${encText}%20${enc}` },
    { name: "Telegram", Icon: Send, color: "#229ED9", href: `https://t.me/share/url?url=${enc}&text=${encText}` },
    { name: "Email", Icon: Mail, color: "#0D5C75", href: `mailto:?subject=${encodeURIComponent(title)}&body=${encText}%0A%0A${enc}` },
  ];

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch {}
  };
  const nativeShare = async () => {
    if (navigator.share) { try { await navigator.share({ title, text, url: link }); } catch {} }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="share-modal">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-head text-xl font-bold text-slate-900 flex items-center gap-2"><Share2 className="w-5 h-5 text-teal" /> Share This Page</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="share-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex justify-center mb-5">
          <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm">
            <QRCodeCanvas value={link} size={148} fgColor="#0D5C75" level="M" includeMargin={false} data-testid="share-qr" />
          </div>
        </div>
        <p className="text-center text-xs text-slate-400 mb-5">Scan to open this page on a phone</p>

        <div className="grid grid-cols-5 gap-2 mb-5">
          {targets.map(({ name, Icon, color, href }) => (
            <a key={name} href={href} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1.5 group" data-testid={`share-${name.toLowerCase()}`}>
              <span className="w-12 h-12 rounded-full flex items-center justify-center text-white transition-transform group-hover:-translate-y-0.5" style={{ backgroundColor: color }}><Icon className="w-5 h-5" /></span>
              <span className="text-[11px] text-slate-500">{name}</span>
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2 bg-slate-100 rounded-xl p-2">
          <span className="flex-1 truncate text-sm text-slate-600 pl-2" data-testid="share-link">{link}</span>
          <button onClick={copy} className="inline-flex items-center gap-1.5 bg-teal hover:bg-teal-dark text-white text-sm font-semibold px-4 py-2 rounded-lg" data-testid="share-copy">
            {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
          </button>
        </div>

        {typeof navigator !== "undefined" && navigator.share && (
          <button onClick={nativeShare} className="w-full mt-3 border border-slate-300 text-slate-600 rounded-xl py-2.5 text-sm font-medium hover:bg-slate-50" data-testid="share-native">More sharing options…</button>
        )}
      </div>
    </div>
  );
}

export function ShareButton({ className = "", label = "Share This Page", url, title, text, "data-testid": testid = "share-button" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} className={className} data-testid={testid}>
        <Share2 className="w-4 h-4" /> {label}
      </button>
      <ShareModal open={open} onClose={() => setOpen(false)} url={url} title={title} text={text} />
    </>
  );
}

export default ShareModal;
